import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from "react";

import {
  createWorkspaceState,
  PANEL_QUERY_REGISTRY,
  resolveWorkspacePanelQuery,
  WORKSPACE_ACTIONS,
  workspaceReducer,
} from "./WorkspaceEngine";

export default function useWorkspaceEngine({
  searchParams,
  setSearchParams,
  getPanelConfig,
  defaultTab = "users",
  panelQueryRegistry = PANEL_QUERY_REGISTRY,
} = {}) {
  const [state, dispatch] = useReducer(
    workspaceReducer,
    createWorkspaceState({
      activeTab: defaultTab,
    })
  );

  const resetWorkspace = useCallback(() => {
    dispatch({
      type: WORKSPACE_ACTIONS.RESET,
    });

    setSearchParams?.({});
  }, [setSearchParams]);

  const changeTab = useCallback(
    (tabKey) => {
      dispatch({
        type: WORKSPACE_ACTIONS.CHANGE_TAB,
        tabKey,
      });

      setSearchParams?.({});
    },
    [setSearchParams]
  );

  const openPanel = useCallback(
    (
      panelMode,
      tabKey,
      panelContext = null,
    ) => {
      dispatch({
        type: WORKSPACE_ACTIONS.OPEN_PANEL,
        panelMode,
        tabKey: tabKey || state.activeTab,
        panelContext,
      });
    },
    [state.activeTab]
  );

  const closePanel = useCallback(() => {
    const panelConfig = getPanelConfig?.(state.panelMode);

    dispatch({
      type: WORKSPACE_ACTIONS.CLOSE_PANEL,
    });

    if (panelConfig?.clearSearchParamsOnClose) {
      setSearchParams?.({});
    }
  }, [
    getPanelConfig,
    setSearchParams,
    state.panelMode,
  ]);

  const toggleFullscreen = useCallback(() => {
    dispatch({
      type: WORKSPACE_ACTIONS.TOGGLE_FULLSCREEN,
    });
  }, []);

  useEffect(() => {
    const panelQuery = searchParams?.get?.("panel");
    const resolvedPanel = resolveWorkspacePanelQuery(
      panelQuery,
      panelQueryRegistry
    );

    if (!resolvedPanel) {
      return;
    }

    dispatch({
      type: WORKSPACE_ACTIONS.SYNC_PANEL,
      tabKey: resolvedPanel.tab,
      panelMode: resolvedPanel.panelMode,
    });
  }, [panelQueryRegistry, searchParams]);

  return useMemo(
    () => ({
      activeTab: state.activeTab,
      panelMode: state.panelMode,
      panelContext: state.panelContext,
      panelKey: state.panelKey,
      isFullscreen: state.isFullscreen,
      isPanelExpanded: state.panelMode !== "info",

      changeTab,
      openPanel,
      closePanel,
      resetWorkspace,
      toggleFullscreen,
    }),
    [
      changeTab,
      closePanel,
      openPanel,
      resetWorkspace,
      state.activeTab,
      state.isFullscreen,
      state.panelContext,
      state.panelKey,
      state.panelMode,
      toggleFullscreen,
    ]
  );
}
